import React, { useState } from 'react';
import { submitWebform } from '../lib/cmsClient';
import FileUploadField from './FileUploadField';

export default function ContactForm({
  idPrefix = 'contact',
  title = 'Kontakt os',
  subtitle = 'Udfyld formularen nedenunder, så vender vi tilbage hurtigst muligt.',
  submitButtonText = 'Indsend henvendelse',
  onSuccess
}) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    postalCode: '',
    message: '',
    financingReady: false,
    acceptTerms: false,
    website_url: ''
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.acceptTerms) {
      setError('Du skal acceptere betingelserne for at indsende formularen.');
      return;
    }
    setSubmitting(true);
    setError('');

    try {
      let res;
      if (files.length > 0) {
        const data = new FormData();
        data.append('Navn', formData.name);
        data.append('Mail', formData.email);
        data.append('email', formData.email); // For regel 12
        data.append('Mobil', formData.phone);
        data.append('Adresse', formData.address);
        data.append('Postnummer', formData.postalCode);
        data.append('Besked', formData.message);
        data.append('Finansiering', formData.financingReady ? 'Ja' : 'Nej');
        data.append('website_url', formData.website_url); // Honeypot
        files.forEach((file) => {
          data.append('billeder', file);
        });
        res = await submitWebform(data);
      } else {
        const payload = {
          Navn: formData.name,
          Mail: formData.email,
          email: formData.email,
          Mobil: formData.phone,
          Adresse: formData.address,
          Postnummer: formData.postalCode,
          Besked: formData.message,
          Finansiering: formData.financingReady ? 'Ja' : 'Nej',
          website_url: formData.website_url
        };
        res = await submitWebform(payload);
      }

      if (res && res.ok) {
        setSubmitted(true);
        setFiles([]);
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          postalCode: '',
          message: '',
          financingReady: false,
          acceptTerms: false,
          website_url: ''
        });
        if (onSuccess) onSuccess();
      } else {
        setError('Der opstod en fejl ved afsendelse. Prøv venligst igen.');
      }
    } catch {
      setError('Der opstod en fejl ved afsendelse. Prøv venligst igen.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {(title || subtitle) && (
        <div className="mb-6 space-y-2">
          {title && (
            <h3 className="text-2xl font-extrabold text-stone-900 uppercase">
              {title}
            </h3>
          )}
          {subtitle && (
            <p className="text-stone-600 text-sm">
              {subtitle}
            </p>
          )}
        </div>
      )}

      {submitted ? (
        <div className="p-6 bg-emerald-50 border border-emerald-200 text-emerald-800 space-y-2">
          <h4 className="font-bold text-lg">Tak for din henvendelse!</h4>
          <p className="text-sm">Vi har modtaget dine oplysninger og kontakter dig hurtigst muligt.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Honeypot (Regel 12) */}
          <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
            <label htmlFor={`${idPrefix}-website_url`}>Website</label>
            <input
              type="text"
              id={`${idPrefix}-website_url`}
              name="website_url"
              value={formData.website_url}
              onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div>
            <label htmlFor={`${idPrefix}-name`} className="block text-xs font-bold text-stone-700 uppercase mb-1">
              Navn*
            </label>
            <input
              id={`${idPrefix}-name`}
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2.5 border border-stone-300 focus:outline-none focus:border-[#BE0F12] text-sm"
              placeholder="Dit fulde navn"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor={`${idPrefix}-email`} className="block text-xs font-bold text-stone-700 uppercase mb-1">
                Mail*
              </label>
              <input
                id={`${idPrefix}-email`}
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2.5 border border-stone-300 focus:outline-none focus:border-[#BE0F12] text-sm"
                placeholder="din@mail.dk"
              />
            </div>
            <div>
              <label htmlFor={`${idPrefix}-phone`} className="block text-xs font-bold text-stone-700 uppercase mb-1">
                Mobil*
              </label>
              <input
                id={`${idPrefix}-phone`}
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2.5 border border-stone-300 focus:outline-none focus:border-[#BE0F12] text-sm"
                placeholder="Mobilnummer"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label htmlFor={`${idPrefix}-address`} className="block text-xs font-bold text-stone-700 uppercase mb-1">
                Adresse*
              </label>
              <input
                id={`${idPrefix}-address`}
                type="text"
                required
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full px-4 py-2.5 border border-stone-300 focus:outline-none focus:border-[#BE0F12] text-sm"
                placeholder="Vejnavn og husnummer"
              />
            </div>
            <div>
              <label htmlFor={`${idPrefix}-postalCode`} className="block text-xs font-bold text-stone-700 uppercase mb-1">
                Postnummer*
              </label>
              <input
                id={`${idPrefix}-postalCode`}
                type="text"
                required
                value={formData.postalCode}
                onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                className="w-full px-4 py-2.5 border border-stone-300 focus:outline-none focus:border-[#BE0F12] text-sm"
                placeholder="F.eks. 8000"
              />
            </div>
          </div>

          <div>
            <label htmlFor={`${idPrefix}-message`} className="block text-xs font-bold text-stone-700 uppercase mb-1">
              Besked*
            </label>
            <textarea
              id={`${idPrefix}-message`}
              rows={4}
              required
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-4 py-2.5 border border-stone-300 focus:outline-none focus:border-[#BE0F12] text-sm"
              placeholder="Beskriv dit projekt her..."
            ></textarea>
          </div>

          {/* Filvedhæftning */}
          <FileUploadField
            files={files}
            onFilesChange={setFiles}
            idPrefix={idPrefix}
            label="Vedhæft evt. 1-5 billeder af dit hus på afstand, hvor man kan se taget"
          />

          <div className="space-y-3 pt-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.financingReady}
                onChange={(e) => setFormData({ ...formData, financingReady: e.target.checked })}
                className="mt-1 h-4 w-4 rounded-none text-[#BE0F12] focus:ring-0"
              />
              <span className="text-xs text-stone-700">
                Har du finansieringen på plads og ønsker et tilbud?
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                required
                checked={formData.acceptTerms}
                onChange={(e) => setFormData({ ...formData, acceptTerms: e.target.checked })}
                className="mt-1 h-4 w-4 rounded-none text-[#BE0F12] focus:ring-0"
              />
              <span className="text-xs text-stone-700">
                Jeg har læst og accepteret vilkårene for brug af oplysninger og privatlivspolitik.*
              </span>
            </label>
          </div>

          {error && (
            <p className="text-xs text-red-600 font-semibold">{error}</p>
          )}

          <div className="pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto px-10 py-3.5 bg-[#BE0F12] hover:bg-[#a00d0f] text-white font-bold text-sm tracking-wider uppercase transition-colors disabled:opacity-50 cursor-pointer shadow-md"
            >
              {submitting ? 'Sender...' : submitButtonText}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
